import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import * as _ from 'lodash-es';
import { Delta } from 'quill/core';
import React, { useCallback, useState } from 'react';
import { useDebounce } from 'use-debounce';
import * as styles from './PublicSearchModal.module.scss';
import { useAppDispatch } from '../app/redux/useAppSelector';
import Breadcrumbs from '../components/Breadcrumbs';
import type { BreadcrumbItem } from '../components/Breadcrumbs';
import Button from '../components/Button';
import DeltaPreview from '../components/DeltaPreview';
import IconButton from '../components/IconButton';
import LoadingIndicator from '../components/LoadingIndicator';
import Modal from '../components/Modal';
import SVGIcon from '../components/SVGIcon';
import { gql } from '../graphql/generated';
import type { DocumentType } from '../graphql/generated';
import closeIcon from '../icons/non-prefilled/16x16/clear.svg';
import searchIcon from '../icons/non-prefilled/16x16/search.svg';
import PostIcon from '../post/PostIcon';
import TopicIcon from '../topic/TopicIcon';
import { UIActions } from '../ui/uiSlice';
import NestableLink from '../utils/NestableLink';
import { useIsSize } from '../utils/responsive/isSize';
import Sizes from '../utils/responsive/Sizes';
import renderHighlight from '../utils/search/renderHighlight';
import { publicPostPath } from '../utils/url/postPath';
import { publicTopicPath } from '../utils/url/topicPath';

const atomWithLocalStorage = <T,>(key: string, initialValue: T) => {
  const getInitialValue = () => {
    const item = localStorage.getItem(key);
    if (item !== null) {
      try {
        return JSON.parse(item) as T;
      } catch (err) {}
    }
    return initialValue;
  };
  const baseAtom = atom(getInitialValue());
  const derivedAtom = atom(
    (get) => get(baseAtom),
    (get, set, update: (current: T) => T) => {
      const nextValue = update(get(baseAtom));
      set(baseAtom, nextValue);
      localStorage.setItem(key, JSON.stringify(nextValue));
    },
  );
  return derivedAtom;
};

const RECENTLY_VIEWED_POST_IDS_KEY = 'public_recently_post_ids';

const recentlyViewedPostIdsAtom = atomWithLocalStorage<string[]>(
  RECENTLY_VIEWED_POST_IDS_KEY,
  [],
);

const PUBLIC_RECENT_POSTS_QUERY = gql(/** @complexity 16 */ `
  query PublicRecentPostsQuery($ids: [ID!]!) {
    publicPosts(ids: $ids) {
      id
      title
      content {
        id
        delta
      }
      ...PublicBreadcrumbs_post
    }
  }
`);

const usePublicRecentlyViewedPosts = () => {
  const ids = useAtomValue(recentlyViewedPostIdsAtom);

  const { data } = useQuery(PUBLIC_RECENT_POSTS_QUERY, {
    variables: { ids },
    skip: ids.length === 0,
  });

  return _.compact(
    ids.map((id) => data?.publicPosts.find((post) => post.id === id)),
  );
};

export const useAddPublicRecentlyViewedPost = () => {
  const update = useSetAtom(recentlyViewedPostIdsAtom);

  return useCallback(
    (id: string) => {
      update((current) => _.uniq([id, ...current]).slice(0, 10));
    },
    [update],
  );
};

const SEARCH_QUERY = gql(/** @complexity 36 */ `
  query PublicSearchQuery($query: String!) {
    publicSearch(query: $query, first: 10) {
      edges {
        node {
          __typename
          ... on PublicPostSearchResult {
            post {
              id
              ...publicPostPath_post
              ...PublicBreadcrumbs_post
            }
            content
            title
            highlight
          }
          ... on PublicTopicSearchResult {
            topic {
              id
              ...publicTopicPath_topic
            }
            name
            description
          }
        }
      }
    }
  }
`);

const PUBLIC_BREADCRUMBS_POST_FRAGMENT = gql(`
  fragment PublicBreadcrumbs_post on PublicPost {
    id
    topics {
      id
      name
      ancestors {
        id
        name
        ...publicTopicPath_topic
      }
      ...publicTopicPath_topic
    }
  }
`);

interface PublicBreadcrumbsProps {
  post: DocumentType<typeof PUBLIC_BREADCRUMBS_POST_FRAGMENT>;
}

const PublicBreadcrumbs = ({ post }: PublicBreadcrumbsProps) => {
  const topic = post.topics[0];
  if (!topic) return null;

  const items = ([topic, ...topic.ancestors] as const)
    .toReversed()
    .map<BreadcrumbItem>((t) => ({ name: t.name, to: publicTopicPath(t) }));

  return <Breadcrumbs size="small" items={items} showLastCaret />;
};

const PublicSearchModal = () => {
  const dispatch = useAppDispatch();
  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebounce(query, 500);

  const recentlyViewedPosts = usePublicRecentlyViewedPosts();

  const { data, loading } = useQuery(SEARCH_QUERY, {
    fetchPolicy: 'no-cache',
    variables: { query: debouncedQuery },
    skip: !debouncedQuery,
  });

  const results = _.compact(
    data?.publicSearch?.edges?.map((edge) => edge?.node),
  );

  const isFullScreen = useIsSize(Sizes.SM);

  return (
    <div
      className={classNames(styles.wrapper, {
        [styles.isFullScreen]: isFullScreen,
      })}
    >
      <Modal noPadding fullscreen={isFullScreen}>
        <div className={styles.container}>
          <div className={styles.searchContainer}>
            <Button className={styles.icon} icon={searchIcon} />
            <input
              autoFocus
              className={styles.searchInput}
              type="text"
              placeholder="Search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <IconButton
              onClick={() => {
                dispatch(UIActions.closeModal());
              }}
              aria-label="Close"
            >
              {closeIcon}
            </IconButton>
          </div>
          {!!debouncedQuery && (
            <div className={styles.results}>
              {loading && (
                <div className={styles.loading}>
                  <LoadingIndicator />
                </div>
              )}
              {!loading && results?.length === 0 && (
                <div className={styles.noResults}>
                  <div className={styles.icon}>
                    <SVGIcon size={24}>{searchIcon}</SVGIcon>
                  </div>
                  <div>
                    No results for <strong>&quot;{debouncedQuery}&quot;</strong>
                  </div>
                </div>
              )}
              {results && results.length > 0 && (
                <>
                  <div className={styles.sectionHeader}>Search Results</div>
                  {results.map((result) => {
                    if (result.__typename === 'PublicPostSearchResult') {
                      const content = new Delta(result.content).compose(
                        new Delta(result.highlight),
                      );

                      return (
                        <NestableLink
                          newTab
                          ignoreInsideLinks
                          key={result.post.id}
                          className={styles.resultRow}
                          to={publicPostPath(result.post)}
                        >
                          <div className={styles.rowIcon}>
                            <PostIcon post={result.post} />
                          </div>
                          <div className={styles.rowTitle}>
                            {renderHighlight(result.title ?? '')}
                          </div>
                          <div className={styles.rowContent}>
                            <DeltaPreview
                              typography="publicSearchResultModal"
                              content={content}
                              maxLines={1}
                            />
                          </div>
                          <div className={styles.rowBreadcrumbs}>
                            <PublicBreadcrumbs post={result.post} />
                          </div>
                        </NestableLink>
                      );
                    }
                    return (
                      <NestableLink
                        newTab
                        key={result.topic.id}
                        className={styles.resultRow}
                        to={publicTopicPath(result.topic)}
                      >
                        <div className={styles.rowIcon}>
                          <TopicIcon />
                        </div>
                        <div className={styles.rowTitle}>
                          {renderHighlight(result.name ?? '')}
                        </div>
                        <div className={styles.rowContent}>
                          <DeltaPreview
                            typography="publicSearchResultModal"
                            content={result.description}
                            maxLines={1}
                          />
                        </div>
                      </NestableLink>
                    );
                  })}
                </>
              )}
            </div>
          )}
          {!debouncedQuery && recentlyViewedPosts.length > 0 && (
            <div className={styles.results}>
              <div className={styles.sectionHeader}>Recently Viewed</div>
              {recentlyViewedPosts.map((post) => {
                return (
                  <NestableLink
                    newTab
                    key={post.id}
                    className={styles.resultRow}
                    to={publicPostPath(post)}
                  >
                    <div className={styles.rowIcon}>
                      <PostIcon post={post} />
                    </div>
                    <div className={styles.rowTitle}>{post.title}</div>
                    <div className={styles.rowContent}>
                      <DeltaPreview
                        typography="publicSearchResultModal"
                        content={post.content.delta}
                        maxLines={1}
                      />
                    </div>
                    <div className={styles.rowBreadcrumbs}>
                      <PublicBreadcrumbs post={post} />
                    </div>
                  </NestableLink>
                );
              })}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default PublicSearchModal;
